import React, { useState, useEffect, useMemo, type ReactNode } from 'react';
import NotificationsContext from './context';

import { useInterval } from '../../hooks/useInterval';
import {
  DEVELOPER_ROLE,
  STATUS_PENDING,
} from '../../assets/AppConstants';

import { useAppContext } from '../app';
import { useUserContext } from '../user';
import { getNotificationsRefresh } from '../../services/Notifications';
import { useWorkspaceContext } from '../workspace';

// Local dev toggle (startup config, read by Vite at dev-server boot): notification polling is ON by default.
// Set VITE_NOTIFICATIONS_POLLING='FALSE' (e.g. in .env.local-be) to silence it locally — no initial fetch, no interval.
const NOTIFICATIONS_POLLING_ENABLED =
  String(import.meta.env.VITE_NOTIFICATIONS_POLLING ?? 'TRUE').toUpperCase() !== 'FALSE';

const NOTIFICATIONS_POLLING_INTERVAL_MS = 20000;
interface NotificationsContextProviderProps {
  children: ReactNode;
}

const NotificationsContextProvider = ({ children }: NotificationsContextProviderProps) => {
  const {
    userSession,
    userIsLogged,
    getUserStatus,
  } = useUserContext();

  const {
    hasRequestStatus,
  } = useAppContext();
  const {
    workspace: {
      defaultWorkspace,
      getWorkspaceRole,
    },
  } = useWorkspaceContext();
  const [newNotifications, setNewNotifications] = useState(0);

  const reloadNotifications = async () => {
    if (!NOTIFICATIONS_POLLING_ENABLED) return;

    const hasWorkspaceUserRole = userIsLogged() && defaultWorkspace?.currentUser?.role;
    const userIsPending = getUserStatus() === STATUS_PENDING;
    const userIsDeveloper = getWorkspaceRole() === DEVELOPER_ROLE;

    const canReloadNotifications = (
      hasWorkspaceUserRole && !userIsPending && !userIsDeveloper && !hasRequestStatus()
    );

    if (!canReloadNotifications) return;

    try {
      const unreadNotifications = await getNotificationsRefresh({ page: 0, onlyUnread: true });
      setNewNotifications(unreadNotifications?.totalElements);
    } catch (_error) {
      // ignora
    }
  };

  useEffect(() => {
    reloadNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, defaultWorkspace]);

  useInterval(() => {
    reloadNotifications();
  }, NOTIFICATIONS_POLLING_ENABLED ? NOTIFICATIONS_POLLING_INTERVAL_MS : null);

  const notificationsValue = useMemo(() => ({
    newNotifications,
    changeNewNotifications: setNewNotifications,
  }), [newNotifications]);

  return (
    <NotificationsContext.Provider value={notificationsValue}>
      {children}
    </NotificationsContext.Provider>
  );
};
export default NotificationsContextProvider;
